import { ICurrentDocument } from '@app/_shared/interface/document.interface';

const KEY_PREFIX = 'PEOPLE_FORM';

const getStorageKey = (key: string): string => {
  return `${KEY_PREFIX}_${key.replace(/-/g, '_').toUpperCase()}`;
};

/**
 * Keys owned by third-party SDKs that must survive a storage clear.
 * Mixpanel keeps its `$device_id`/`distinct_id` under `mp_<token>_mixpanel`; wiping it on
 * logout makes the SDK mint a brand new anonymous device on the next page load, which
 * inflates Monthly Tracked Users and breaks identity merge for returning users.
 *
 * These keys are skipped by removing every other key one by one, never by calling
 * `storage.clear()` and writing them back: a full clear fires a global storage event with a
 * null key in the other tabs, and leaves a window where the preserved key does not exist.
 */
const PRESERVED_KEY_PREFIXES = ['mp_'];

const isPreservedKey = (key: string): boolean =>
  PRESERVED_KEY_PREFIXES.some(prefix => key.startsWith(prefix));

const getRemovableKeys = (storage: Storage): string[] => {
  const keys: string[] = [];

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key && !isPreservedKey(key)) {
      keys.push(key);
    }
  }

  return keys;
};

export const createStorage = (storage: Storage) => ({
  setItem(key: string, value: any): void {
    try {
      const storageKey = getStorageKey(key);
      const data = JSON.stringify(value);
      storage.setItem(storageKey, data);
    } catch (error) {
      console.error('Error setting data in storage:', error);
    }
  },
  getItem(key: string): any {
    try {
      const storageKey = getStorageKey(key);
      const data = storage.getItem(storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error getting data from storage:', error);
      return null;
    }
  },
  removeItem(key: string): void {
    try {
      const storageKey = getStorageKey(key);
      storage.removeItem(storageKey);
    } catch (error) {
      console.error('Error removing data from storage:', error);
    }
  },
  clear(): void {
    try {
      getRemovableKeys(storage).forEach(key => storage.removeItem(key));
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }
});

export const SessionStorage = createStorage(sessionStorage);
export const LocalStorage = createStorage(localStorage);

export const clearStorage = (): void => {
  LocalStorage.clear();
  SessionStorage.clear();
};

export const setTokenStorage = (token = ''): void => {
  LocalStorage.setItem('token', token);
};

export const setCsrfToken = (csrfToken = ''): void => {
  LocalStorage.setItem('csrf_token', csrfToken);
};

export const setCurrentUser = (user: any): void => {
  SessionStorage.setItem('user', user);
};

export const setLastDocument = (document = ''): void => {
  SessionStorage.setItem('lastDocument', document);
};

export const setCurrentAccount = (account = ''): void => {
  SessionStorage.setItem('current_account', account);
};

export const setCurrentOrganization = (organization = ''): void => {
  SessionStorage.setItem('originOrganizationId', organization);
};

export const setPeoplePositionId = (peoplePositionId = ''): void => {
  SessionStorage.setItem('originPositionId', peoplePositionId);
};

export const setCpfId = (cpfId = ''): void => {
  SessionStorage.setItem('cpfId', cpfId);
};

export const getTokenStorage = (): string | null => {
  return LocalStorage.getItem('token');
};

export const getCsrfToken = (): string | null => {
  return LocalStorage.getItem('csrf_token');
};

export const getCurrentUser = (): any => {
  return SessionStorage.getItem('user') || {};
};

export const getLastDocument = (): string | null => {
  return SessionStorage.getItem('lastDocument');
};

export const getCurrentAccount = (): string | null => {
  return SessionStorage.getItem('current_account');
};

export const getCurrentOrganization = (): string | null => {
  return SessionStorage.getItem('originOrganizationId');
};

export const getOrganizationName = (): string | null => {
  return SessionStorage.getItem('user')?.organizationName || '';
};

export const getPeoplePositionId = (): string | null => {
  return SessionStorage.getItem('originPositionId');
};

export const getCurrentDocument = (): ICurrentDocument | null => {
  return SessionStorage.getItem('current-document');
};

export const setCurrentDocument = (data: ICurrentDocument): void => {
  SessionStorage.setItem('current-document', data);
};

export const clearCurrentDocument = (): void => {
  SessionStorage.removeItem('current-document');
};